// Sanitising HTML that came from outside the firm.
//
// Email bodies arrive from Microsoft Graph, which means they arrive from
// whoever emailed us. A public accounting practice publishes its address, so
// this input is attacker-controlled by design. Rendering it with
// dangerouslySetInnerHTML — which is what OutlookPage and ClientEmailsTab did —
// let any sender run script on the CRM's origin with the reading staff member's
// Firebase session.
//
// innerHTML does not execute <script>, which is why this went unnoticed. It does
// fire <img src=x onerror=…>, <svg onload=…> and <iframe src="javascript:…">.

import DOMPurify from "dompurify";

/**
 * Strip anything executable from an email body.
 *
 * Deliberately strict:
 *
 * - All `on*` handlers go. This is the actual attack surface.
 * - `<form>` goes, so a mail cannot render a credential prompt that looks like
 *   part of the CRM.
 * - Remote images are left in place. Blocking them would be the stricter choice
 *   and would stop senders using them as read receipts, but it changes what
 *   staff see in a mail client they rely on. Worth revisiting deliberately
 *   rather than as a side effect of a security fix.
 * - Links survive, but every anchor is forced to open in a new tab with
 *   rel="noopener noreferrer" so the CRM's URL is not handed to the target.
 */
export function sanitizeEmailHtml(dirty) {
  if (!dirty || typeof dirty !== "string") return "";

  return DOMPurify.sanitize(dirty, {
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "base", "link", "meta"],
    FORBID_ATTR: ["srcdoc", "formaction", "ping"],
    // javascript: and data: URLs in href/src, which are the usual way round a
    // tag filter.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // Keep the document fragment only — no <html>/<body> wrapper.
    WHOLE_DOCUMENT: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });
}

// DOMPurify hooks are global, so this registers once per page rather than per
// call. An email's links open away from the CRM, never in place.
let hooked = false;
if (typeof window !== "undefined" && !hooked) {
  hooked = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.hasAttribute("href")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

/** Does this body look like HTML at all? Mirrors the old inline check. */
export const looksLikeHtml = (body) =>
  Boolean(body) && body.includes("<") && body.includes(">");

/**
 * Sanitise rich text the firm authors itself — blog posts, and the Froyo AI
 * analysis panel.
 *
 * Looser than the email rules because this is trusted-ish content that needs to
 * keep its formatting, and tighter than nothing because "the firm authored it"
 * is an assumption rather than a guarantee: blog post bodies come out of a
 * contentEditable field, and the analysis body is model output shaped by an
 * uploaded PDF. Applied when a post is SAVED as well as when it is rendered, so
 * what reaches the public marketing site is already clean.
 */
export function sanitizeRichText(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  return DOMPurify.sanitize(dirty, {
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "base", "link", "meta"],
    FORBID_ATTR: ["srcdoc", "formaction", "ping"],
  });
}
